import { useScrollReveal } from "@/hooks/useScrollReveal";
import { leadershipTeam, globalOffices } from "@/mocks/homeData";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";

function SectionReveal({
  children,
  className = "",
  delay = 0,
}: {
  children: React.ReactNode;
  className?: string;
  delay?: number;
}) {
  const { ref, isVisible } = useScrollReveal();
  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ${className} ${
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
      }`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}

const regionFlags: Record<string, string> = {
  INDIA: "https://flagcdn.com/w80/in.png",
  USA: "https://flagcdn.com/w80/us.png",
  UAE: "https://flagcdn.com/w80/ae.png",
  SINGAPORE: "https://flagcdn.com/w80/sg.png",
};

export default function About() {
  return (
    <>
      <Helmet>
        <title>
          About Azosis Technologies USA — Technology Consulting Partner | United
          States
        </title>

        <meta
          name="description"
          content="Azosis Technologies is incorporated in Delaware and operates as a technology consulting and managed services partner for enterprises across the United States. US-market focus backed by a global delivery network across India, UAE, and Singapore."
        />

        <meta
          name="keywords"
          content="about Azosis USA, Azosis Technologies Delaware, technology consulting firm USA, IT consulting partner United States, Azosis US operations, managed services USA partner"
        />

        <link rel="canonical" href="https://azosis.us/about/" />

        <meta
          property="og:title"
          content="About Azosis Technologies USA — Technology Consulting Partner"
        />

        <meta
          property="og:description"
          content="Incorporated in Delaware. Technology consulting and managed services for enterprises across the USA — backed by a global delivery network."
        />

        <meta property="og:type" content="website" />

        <meta property="og:url" content="https://azosis.us/about/" />

        <meta
          property="og:image"
          content="https://azosis.us/images/og-image.jpg"
        />

        <meta property="og:site_name" content="Azosis Technologies" />

        <meta name="twitter:card" content="summary_large_image" />

        <meta name="twitter:title" content="About Azosis Technologies USA" />

        <meta
          name="twitter:description"
          content="Technology consulting and managed services for enterprises across the USA. Delaware-incorporated, globally delivered."
        />

        <meta
          name="twitter:image"
          content="https://azosis.us/images/og-image.jpg"
        />

        <meta name="robots" content="index, follow" />
      </Helmet>

      <div className="bg-azosis-dark min-h-screen pt-20 overflow-x-hidden">
        {/* S1 — Org Values (AZ / OS / IS) */}
        <section className="py-8 md:py-12 bg-azosis-surface">
          <div className="w-full px-6 md:px-10 lg:px-16">
            <div className="max-w-4xl mx-auto">
              <SectionReveal>
                <div className="text-center mb-16 md:mb-24">
                  <span className="text-azosis-gold text-xs tracking-[0.3em] uppercase mb-4 block">
                    Our Values
                  </span>
                  <h2 className="font-display text-3xl md:text-4xl font-bold text-white mb-6">
                    The values in the name.
                  </h2>
                </div>
              </SectionReveal>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {[
                  {
                    code: "AZ",
                    name: "Amazing Zeal",
                    desc: "Energy in every engagement. Curiosity at every step.",
                  },
                  {
                    code: "OS",
                    name: "Outstanding Services",
                    desc: "Quality is the standard, beautifully delivered.",
                  },
                  {
                    code: "IS",
                    name: "Integrity in Solutions",
                    desc: "The right answer for the client, every time.",
                  },
                ].map((value, index) => (
                  <SectionReveal key={value.code} delay={index * 150}>
                    <div className="bg-azosis-dark border border-azosis-border p-8 md:p-10 text-center">
                      <div className="w-16 h-16 bg-azosis-gold/10 border border-azosis-gold/30 rounded-sm flex items-center justify-center mx-auto mb-6">
                        <span className="text-azosis-gold font-display font-bold text-2xl">
                          {value.code}
                        </span>
                      </div>
                      <h3 className="text-white font-display text-xl font-bold mb-3">
                        {value.name}
                      </h3>
                      <p className="text-azosis-textSecondary text-sm leading-relaxed">
                        {value.desc}
                      </p>
                    </div>
                  </SectionReveal>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* S3 — Our Architecture */}
        <section className="py-8 md:py-12 bg-azosis-dark">
          <div className="w-full px-6 md:px-10 lg:px-16">
            <div className="max-w-6xl mx-auto">
              <SectionReveal>
                <div className="text-center mb-16">
                  <span className="text-azosis-gold text-xs tracking-[0.3em] uppercase mb-4 block">
                    Our Architecture
                  </span>
                  <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
                    The name is the architecture.
                  </h2>
                </div>
              </SectionReveal>

              {/* Architecture Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
                {[
                  {
                    code: "AZ",
                    title: "AZ · Complete Analysis",
                    engine: "Business Outcomes",
                  },
                  {
                    code: "OS",
                    title: "OS · Outcomes Driven",
                    engine: "Optimised Execution",
                  },
                  {
                    code: "IS",
                    title: "IS · Intelligence System",
                    engine: "Continuous Growth via Technology",
                  },
                ].map((item, index) => (
                  <SectionReveal key={item.code} delay={index * 150}>
                    <div className="bg-azosis-surface border border-azosis-border p-8 md:p-10 h-full flex flex-col">
                      <div className="w-14 h-14 bg-azosis-gold/10 border border-azosis-gold/30 rounded-sm flex items-center justify-center mb-6">
                        <span className="text-azosis-gold font-display font-bold text-xl">
                          {item.code}
                        </span>
                      </div>
                      <h3 className="text-white font-display text-lg md:text-xl font-bold mb-3">
                        {item.title}
                      </h3>
                      <p className="text-azosis-gold text-sm font-semibold tracking-wide uppercase mt-auto">
                        {item.engine}
                      </p>
                    </div>
                  </SectionReveal>
                ))}
              </div>

              {/* Action Items */}
              <SectionReveal>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {[
                    "Technology-agnostic advisory",
                    "Industry and solutions mapping",
                    "Cost optimisation",
                    "Managed service provider",
                  ].map((item, index) => (
                    <div
                      key={index}
                      className="bg-azosis-surface border border-azosis-border px-6 py-5 text-center hover:border-azosis-gold/30 transition-colors"
                    >
                      <span className="text-azosis-textSecondary text-sm">
                        {item}
                      </span>
                    </div>
                  ))}
                </div>
              </SectionReveal>
            </div>
          </div>
        </section>

        {/* S4 — Key Solution Pillars */}
        <section className="py-8 md:py-12 bg-azosis-surface">
          <div className="w-full px-6 md:px-10 lg:px-16">
            <div className="max-w-6xl mx-auto">
              <SectionReveal>
                <div className="text-center mb-16 md:mb-24">
                  <span className="text-azosis-gold text-xs tracking-[0.3em] uppercase mb-4 block">
                    Solution Pillars
                  </span>
                  <h2 className="font-display text-3xl md:text-4xl font-bold text-white mb-6">
                    Key Solution Pillars
                  </h2>
                </div>
              </SectionReveal>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {[
                  {
                    num: "01",
                    title: "Infrastructure & Security Solutions",
                    desc: "Resilient foundations - cloud, network, identity, and zero-trust security across hybrid environments.",
                    aligned: "AZ",
                  },
                  {
                    num: "02",
                    title: "Managed Services & Consulting",
                    desc: "Continuous delivery - managed operations, strategic advisory, and adoption excellence.",
                    aligned: "OS",
                  },
                  {
                    num: "03",
                    title: "Innovation & Solution Platforms",
                    desc: "Hybrid intelligence - AI, data, automation, and digital platforms that amplify human expertise.",
                    aligned: "IS",
                  },
                ].map((pillar, index) => (
                  <SectionReveal key={pillar.num} delay={index * 150}>
                    <div className="bg-azosis-dark border border-azosis-border p-8 md:p-10">
                      <div className="flex items-center gap-2 mb-4">
                        <span className="text-azosis-gold font-bold text-sm">
                          {pillar.aligned}
                        </span>
                        <span className="w-4 h-px bg-azosis-gold/50"></span>
                        <span className="text-azosis-textMuted text-xs uppercase tracking-wide">
                          Aligned to {pillar.aligned}
                        </span>
                      </div>
                      <div className="text-azosis-textMuted text-xs mb-3">
                        Pillar {pillar.num}
                      </div>
                      <h3 className="text-white font-semibold text-base mb-3">
                        {pillar.title}
                      </h3>
                      <p className="text-azosis-textSecondary text-sm leading-relaxed">
                        {pillar.desc}
                      </p>
                    </div>
                  </SectionReveal>
                ))}
              </div>
            </div>
          </div>
        </section>

        
        {/* S7 — Bottom CTA */}
        <section className="py-8 md:py-12 bg-azosis-surface relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 right-0 w-96 h-96 bg-azosis-gold/20 rounded-full blur-[150px]"></div>
          </div>
          <div className="relative z-10 w-full px-6 md:px-10 lg:px-16">
            <div className="max-w-3xl mx-auto text-center">
              <SectionReveal>
                <h2 className="font-display text-3xl md:text-4xl font-bold text-white mb-6">
                  Ready to work with our US team?
                </h2>
                <p className="text-azosis-textSecondary text-base md:text-lg leading-relaxed mb-10">
                  Whether you are an enterprise modernising your operations, a
                  founder building the next category-defining venture, a
                  public-sector body pursuing digital reform, or a growth-stage
                  firm expanding capabilities in parallel with operations -
                  Always present. Always active. Always now.
                </p>
                <div className="flex items-center justify-center">
                  <Link
                    to="/contact"
                    className="inline-flex items-center gap-2 px-8 py-4 bg-azosis-gold text-azosis-dark font-semibold text-sm tracking-wide hover:bg-azosis-goldLight transition-all duration-300 whitespace-nowrap"
                  >
                    Start a Conversation
                    <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
              </SectionReveal>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
